import type { Language } from "./locale";

export type AudioChapter = {
  time: number;
  label: Record<Language, string>;
};

export type EssayAudioCopy = {
  label: string;
  title: string;
  description: string;
  playerLabel: string;
  chapters: string;
  transcript: string;
  download: string;
  play: string;
  pause: string;
  close: string;
  seek: string;
  transcriptEyebrow: string;
  transcriptTitle: string;
  transcriptLead: string;
  transcriptNote: string;
  transcriptLanguage: string;
  englishOriginal: string;
  ukrainianTranslation: string;
  russianTranslation: string;
  loading: string;
  loadError: string;
  retry: string;
  backToEssay: string;
  jumpToTime: (time: string) => string;
};

export type EssayAudio = {
  slug: string;
  audioId: string;
  essayNumber: string;
  duration: number;
  publicDirectory: string;
  downloadFilename: string;
  sources: {
    opus: string;
    mp3: string;
  };
  chapters: readonly AudioChapter[];
  copy: Record<Language, EssayAudioCopy>;
};

type LocalizedAudioText = Record<Language, string>;

function standardAudioCopy({
  essayNumber,
  durationLabel,
  audioTitle,
  essayTitle,
}: {
  essayNumber: string;
  durationLabel: string;
  audioTitle: LocalizedAudioText;
  essayTitle: LocalizedAudioText;
}): Record<Language, EssayAudioCopy> {
  return {
    en: {
      label: `Audio conversation · ${durationLabel} · English`,
      title: audioTitle.en,
      description: `A two-host discussion of ${essayTitle.en}, created with Google NotebookLM and checked against the original essay for semantic accuracy. This is an interpretation of the argument, not a verbatim reading.`,
      playerLabel: `Play the English audio conversation ${audioTitle.en}`,
      chapters: "Listen by idea",
      transcript: "Full transcript",
      download: "Download MP3",
      play: "Play audio",
      pause: "Pause audio",
      close: "Close and stop the compact player",
      seek: "Audio progress",
      transcriptEyebrow: `Audio companion · Essay ${essayNumber}`,
      transcriptTitle: audioTitle.en,
      transcriptLead: "Listen while reading the complete transcript. Choose a language below; every timestamp jumps to the exact moment in the English conversation.",
      transcriptNote: "This conversation was created with Google NotebookLM and checked against the original essay. It interprets the argument; it is not a verbatim reading of the essay.",
      transcriptLanguage: "Transcript language",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Loading the transcript…",
      loadError: "The transcript could not be loaded. Please try again, or open the English original.",
      retry: "Try again",
      backToEssay: `Return to Essay ${essayNumber}`,
      jumpToTime: (time: string) => `Play from ${time}`,
    },
    uk: {
      label: `Аудіорозмова · ${durationLabel} · англійською`,
      title: audioTitle.uk,
      description: `Розмова двох ведучих про есе «${essayTitle.uk}», створена за допомогою Google NotebookLM і перевірена на відповідність змісту оригіналу. Це інтерпретація аргументу, а не дослівне читання есе.`,
      playerLabel: `Відтворити англомовну аудіорозмову ${audioTitle.uk}`,
      chapters: "Слухати за темами",
      transcript: "Повна транскрипція",
      download: "Завантажити MP3",
      play: "Відтворити аудіо",
      pause: "Призупинити аудіо",
      close: "Закрити й зупинити компактний програвач",
      seek: "Перебіг аудіо",
      transcriptEyebrow: `Аудіосупровід · Есе ${essayNumber}`,
      transcriptTitle: audioTitle.uk,
      transcriptLead: "Слухайте й читайте повну транскрипцію. Оберіть мову нижче; кожен таймкод веде до точного місця в англомовній розмові.",
      transcriptNote: "Цю розмову створено за допомогою Google NotebookLM і перевірено за оригінальним есе. Вона інтерпретує аргумент, а не читає есе дослівно.",
      transcriptLanguage: "Мова транскрипції",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Завантажуємо транскрипцію…",
      loadError: "Не вдалося завантажити транскрипцію. Спробуйте ще раз або відкрийте англійський оригінал.",
      retry: "Спробувати ще раз",
      backToEssay: `Повернутися до есе ${essayNumber}`,
      jumpToTime: (time: string) => `Відтворити з ${time}`,
    },
    ru: {
      label: `Аудиоразговор · ${durationLabel} · на английском`,
      title: audioTitle.ru,
      description: `Разговор двух ведущих об эссе «${essayTitle.ru}», созданный с помощью Google NotebookLM и проверенный на соответствие оригиналу. Это интерпретация аргумента, а не дословное чтение эссе.`,
      playerLabel: `Воспроизвести англоязычный аудиоразговор ${audioTitle.ru}`,
      chapters: "Слушать по темам",
      transcript: "Полная транскрипция",
      download: "Скачать MP3",
      play: "Воспроизвести аудио",
      pause: "Приостановить аудио",
      close: "Закрыть и остановить компактный проигрыватель",
      seek: "Ход аудио",
      transcriptEyebrow: `Аудиосопровождение · Эссе ${essayNumber}`,
      transcriptTitle: audioTitle.ru,
      transcriptLead: "Слушайте и читайте полную транскрипцию. Выберите язык ниже; каждый таймкод ведёт к точному месту в англоязычном разговоре.",
      transcriptNote: "Этот разговор создан с помощью Google NotebookLM и проверен по оригинальному эссе. Он интерпретирует аргумент, а не читает эссе дословно.",
      transcriptLanguage: "Язык транскрипции",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Загружаем транскрипцию…",
      loadError: "Не удалось загрузить транскрипцию. Попробуйте ещё раз или откройте английский оригинал.",
      retry: "Попробовать ещё раз",
      backToEssay: `Вернуться к эссе ${essayNumber}`,
      jumpToTime: (time: string) => `Воспроизвести с ${time}`,
    },
  };
}

const essay01Audio = {
  slug: "map-knows-it-is-map",
  audioId: "essay-01-map",
  essayNumber: "01",
  duration: 1348.638186,
  publicDirectory: "/audio/essay-01",
  downloadFilename: "how-a-map-can-correct-itself.v1.mp3",
  chapters: [
    { time: 0, label: { en: "Why useful maps leave things out", uk: "Чому корисні мапи щось залишають поза увагою", ru: "Почему полезные карты что-то оставляют за рамками" } },
    { time: 99, label: { en: "Beyond pure facts and relativism", uk: "Між чистими фактами та релятивізмом", ru: "Между чистыми фактами и релятивизмом" } },
    { time: 208, label: { en: "From event to decision: seven translations", uk: "Від події до рішення: сім перекладів", ru: "От события до решения: семь переводов" } },
    { time: 380, label: { en: "How a trace constrains a story", uk: "Як слід обмежує розповідь", ru: "Как след ограничивает рассказ" } },
    { time: 525, label: { en: "Popper and Kuhn: testing through frames", uk: "Поппер і Кун: перевірка крізь рамки", ru: "Поппер и Кун: проверка через рамки" } },
    { time: 818, label: { en: "Feynman: honesty needs institutions", uk: "Фейнман: чесності потрібні інституції", ru: "Фейнман: честности нужны институты" } },
    { time: 951, label: { en: "Scott: legibility and self-sealing maps", uk: "Скотт: читабельність і самозамкнені мапи", ru: "Скотт: читаемость и самозамыкающиеся карты" } },
    { time: 1145, label: { en: "Safe routes for correction", uk: "Безпечні шляхи для виправлення", ru: "Безопасные пути исправления" } },
  ],
  sources: {
    opus: "/audio/essay-01/how-a-map-can-correct-itself.v1.opus",
    mp3: "/audio/essay-01/how-a-map-can-correct-itself.v1.mp3",
  },
  copy: standardAudioCopy({
    essayNumber: "01",
    durationLabel: "22:28",
    audioTitle: {
      en: "How a Map Can Correct Itself",
      uk: "Як мапа може виправляти себе",
      ru: "Как карта может исправлять себя",
    },
    essayTitle: {
      en: "The Map That Knows It Is a Map",
      uk: "Мапа, яка знає, що вона мапа",
      ru: "Карта, которая знает, что она карта",
    },
  }),
} satisfies EssayAudio;

const essay02Audio = {
  slug: "power-to-define-reality",
  audioId: "essay-02-power",
  essayNumber: "02",
  duration: 1336.37805,
  publicDirectory: "/audio/essay-02",
  downloadFilename: "how-power-shapes-what-becomes-visible.v1.mp3",
  chapters: [
    { time: 0, label: { en: "A truthful report can still hide reality", uk: "Правдивий звіт усе одно може приховувати реальність", ru: "Правдивый отчёт всё равно может скрывать реальность" } },
    { time: 126, label: { en: "Why simplification is necessary—and dangerous", uk: "Чому спрощення необхідне — і небезпечне", ru: "Почему упрощение необходимо — и опасно" } },
    { time: 193, label: { en: "The agenda allocates reality before the vote", uk: "Порядок денний розподіляє реальність до голосування", ru: "Повестка распределяет реальность до голосования" } },
    { time: 395, label: { en: "Categories decide who becomes visible", uk: "Категорії вирішують, хто стає видимим", ru: "Категории решают, кто становится видимым" } },
    { time: 593, label: { en: "Status changes the weight of an argument", uk: "Статус змінює вагу аргументу", ru: "Статус меняет вес аргумента" } },
    { time: 830, label: { en: "Bad news needs a safe route to power", uk: "Поганим новинам потрібен безпечний шлях до влади", ru: "Плохим новостям нужен безопасный путь к власти" } },
    { time: 1013, label: { en: "Arendt and Foucault: two necessary lenses", uk: "Арендт і Фуко: дві необхідні оптики", ru: "Арендт и Фуко: две необходимые оптики" } },
    { time: 1190, label: { en: "Power must remain correctable", uk: "Влада має залишатися виправною", ru: "Власть должна оставаться исправимой" } },
  ],
  sources: {
    opus: "/audio/essay-02/how-power-shapes-what-becomes-visible.v1.opus",
    mp3: "/audio/essay-02/how-power-shapes-what-becomes-visible.v1.mp3",
  },
  copy: standardAudioCopy({
    essayNumber: "02",
    durationLabel: "22:16",
    audioTitle: {
      en: "How Power Shapes What Becomes Visible",
      uk: "Як влада формує те, що стає видимим",
      ru: "Как власть формирует то, что становится видимым",
    },
    essayTitle: {
      en: "Who Has the Right to Define Reality?",
      uk: "Хто отримує право визначати реальність",
      ru: "Кто получает право определить реальность",
    },
  }),
} satisfies EssayAudio;

const essay03Audio = {
  slug: "world-is-not-made-of-games",
  audioId: "essay-03-games",
  essayNumber: "03",
  duration: 1249.44254,
  publicDirectory: "/audio/essay-03",
  downloadFilename: "where-the-game-metaphor-ends.v1.mp3",
  chapters: [
    { time: 0, label: { en: "The promise and danger of calling life a game", uk: "Обіцянка й небезпека називати життя грою", ru: "Обещание и опасность называть жизнь игрой" } },
    { time: 153, label: { en: "The strict test: an umbrella is not a countermove", uk: "Сувора перевірка: парасоля — не хід у відповідь", ru: "Строгая проверка: зонт — не ответный ход" } },
    { time: 298, label: { en: "Schelling: leverage is not moral permission", uk: "Шеллінг: важіль не дає морального дозволу", ru: "Шеллинг: рычаг не даёт морального разрешения" } },
    { time: 416, label: { en: "When accounting deforms friendship and care", uk: "Коли підрахунок спотворює дружбу й турботу", ru: "Когда подсчёт искажает дружбу и заботу" } },
    { time: 647, label: { en: "Coercion, dignity, and power created together", uk: "Примус, гідність і влада, створена разом", ru: "Принуждение, достоинство и власть, созданная вместе" } },
    { time: 834, label: { en: "Ostrom: people can change the rules together", uk: "Остром: люди можуть разом змінювати правила", ru: "Остром: люди могут вместе менять правила" } },
    { time: 937, label: { en: "Why a worthy life has no single scoreboard", uk: "Чому гідне життя не має єдиного табло", ru: "Почему у достойной жизни нет единого табло" } },
    { time: 1102, label: { en: "Three tests for the game metaphor", uk: "Три перевірки для метафори гри", ru: "Три проверки для метафоры игры" } },
  ],
  sources: {
    opus: "/audio/essay-03/where-the-game-metaphor-ends.v1.opus",
    mp3: "/audio/essay-03/where-the-game-metaphor-ends.v1.mp3",
  },
  copy: standardAudioCopy({
    essayNumber: "03",
    durationLabel: "20:49",
    audioTitle: {
      en: "Where the Game Metaphor Ends",
      uk: "Де закінчується метафора гри",
      ru: "Где заканчивается метафора игры",
    },
    essayTitle: {
      en: "The World Is Made of More Than Games",
      uk: "Світ складається не лише з ігор",
      ru: "Мир состоит не только из игр",
    },
  }),
} satisfies EssayAudio;

const essay04Audio = {
  slug: "strategy-as-refusal",
  audioId: "essay-04-refusal",
  essayNumber: "04",
  duration: 1295.998549,
  publicDirectory: "/audio/essay-04",
  downloadFilename: "the-refusals-that-make-strategy-real.v1.mp3",
  chapters: [
    { time: 0, label: { en: "Why an inspiring wish is not yet a strategy", uk: "Чому надихаюче бажання — іще не стратегія", ru: "Почему вдохновляющее желание — ещё не стратегия" } },
    { time: 130, label: { en: "Scarcity and Rumelt's strategy kernel", uk: "Обмеженість і ядро стратегії Румельта", ru: "Ограниченность и ядро стратегии Румельта" } },
    { time: 204, label: { en: "A diagnosis changes action, blame, and cost", uk: "Діагноз змінює дії, провину й ціну", ru: "Диагноз меняет действия, вину и цену" } },
    { time: 389, label: { en: "Opportunity cost makes refusal concrete", uk: "Альтернативна вартість робить відмову конкретною", ru: "Альтернативная стоимость делает отказ конкретным" } },
    { time: 553, label: { en: "The test of a real priority and a decent minimum", uk: "Перевірка справжнього пріоритету й гідного мінімуму", ru: "Проверка реального приоритета и достойного минимума" } },
    { time: 635, label: { en: "Machiavelli, Berlin, and Kant test the means", uk: "Макіавеллі, Берлін і Кант перевіряють засоби", ru: "Макиавелли, Берлин и Кант проверяют средства" } },
    { time: 1008, label: { en: "The coherence trap and the duty to review", uk: "Пастка узгодженості й обов'язок перегляду", ru: "Ловушка согласованности и обязанность пересмотра" } },
    { time: 1108, label: { en: "Accepted cost versus unacceptable harm", uk: "Прийнятна ціна проти неприйнятної шкоди", ru: "Приемлемая цена против неприемлемого вреда" } },
  ],
  sources: {
    opus: "/audio/essay-04/the-refusals-that-make-strategy-real.v1.opus",
    mp3: "/audio/essay-04/the-refusals-that-make-strategy-real.v1.mp3",
  },
  copy: standardAudioCopy({
    essayNumber: "04",
    durationLabel: "21:35",
    audioTitle: {
      en: "The Refusals That Make Strategy Real",
      uk: "Відмови, які роблять стратегію реальною",
      ru: "Отказы, которые делают стратегию реальной",
    },
    essayTitle: {
      en: "Strategy Is the Art of Organized Refusal",
      uk: "Стратегія — мистецтво організованої відмови",
      ru: "Стратегия — искусство организованного отказа",
    },
  }),
} satisfies EssayAudio;

const essay05Audio = {
  slug: "victory-changes-winner",
  audioId: "essay-05-victory",
  essayNumber: "05",
  duration: 1217.724082,
  publicDirectory: "/audio/essay-05",
  downloadFilename: "how-success-changes-the-flow-of-truth.v1.mp3",
  chapters: [
    { time: 0, label: { en: "Why victory feels like proof of wisdom", uk: "Чому перемога здається доказом мудрості", ru: "Почему победа кажется доказательством мудрости" } },
    { time: 124, label: { en: "What an outcome actually proves", uk: "Що результат насправді доводить", ru: "Что результат действительно доказывает" } },
    { time: 167, label: { en: "Survivorship bias and the invisible sample", uk: "Упередження виживання й невидима вибірка", ru: "Ошибка выжившего и невидимая выборка" } },
    { time: 255, label: { en: "Moral luck and four tests of success", uk: "Моральна удача й чотири перевірки успіху", ru: "Моральная удача и четыре проверки успеха" } },
    { time: 389, label: { en: "Victory creates a new game", uk: "Перемога створює нову гру", ru: "Победа создаёт новую игру" } },
    { time: 554, label: { en: "Scale, metrics, and distance from consequences", uk: "Масштаб, метрики й відстань від наслідків", ru: "Масштаб, метрики и дистанция от последствий" } },
    { time: 807, label: { en: "How a court forms around the winner", uk: "Як навколо переможця формується двір", ru: "Как вокруг победителя формируется двор" } },
    { time: 993, label: { en: "The right to be stopped—and the test of succession", uk: "Право бути зупиненим і перевірка наступництвом", ru: "Право быть остановленным и проверка преемственностью" } },
  ],
  sources: {
    opus: "/audio/essay-05/how-success-changes-the-flow-of-truth.v1.opus",
    mp3: "/audio/essay-05/how-success-changes-the-flow-of-truth.v1.mp3",
  },
  copy: standardAudioCopy({
    essayNumber: "05",
    durationLabel: "20:17",
    audioTitle: {
      en: "How Success Changes the Flow of Truth",
      uk: "Як успіх змінює потік правди",
      ru: "Как успех меняет поток правды",
    },
    essayTitle: {
      en: "Victory Changes the Winner",
      uk: "Перемога змінює переможця",
      ru: "Победа меняет победителя",
    },
  }),
} satisfies EssayAudio;

const essay06Audio = {
  slug: "there-is-no-last-arena",
  audioId: "essay-06-last-arena",
  essayNumber: "06",
  duration: 1419.25,
  publicDirectory: "/audio/essay-06",
  downloadFilename: "why-illness-is-not-a-battle.mp3",
  chapters: [
    {
      time: 0,
      label: {
        en: "Why we reach for scoreboards",
        uk: "Чому нас приваблюють табло",
        ru: "Почему нас привлекают табло",
      },
    },
    {
      time: 194,
      label: {
        en: "Where the game metaphor breaks",
        uk: "Де руйнується метафора гри",
        ru: "Где разрушается метафора игры",
      },
    },
    {
      time: 395,
      label: {
        en: "Rejecting the “last arena” is not surrender",
        uk: "Відмова від «останньої арени» — не капітуляція",
        ru: "Отказ от «последней арены» — не капитуляция",
      },
    },
    {
      time: 531,
      label: {
        en: "Agency is not a switch",
        uk: "Агентність — не перемикач",
        ru: "Агентность — не переключатель",
      },
    },
    {
      time: 865,
      label: {
        en: "Frankl and Levi: possibility without duty",
        uk: "Франкл і Леві: можливість без обов’язку",
        ru: "Франкл и Леви: возможность без обязанности",
      },
    },
    {
      time: 1119,
      label: {
        en: "Dignity before outcome",
        uk: "Гідність передує результату",
        ru: "Достоинство предшествует результату",
      },
    },
    {
      time: 1287,
      label: {
        en: "What finitude clarifies",
        uk: "Що прояснює скінченність",
        ru: "Что проясняет конечность",
      },
    },
  ],
  sources: {
    opus: "/audio/essay-06/why-illness-is-not-a-battle.v1.opus",
    mp3: "/audio/essay-06/why-illness-is-not-a-battle.v1.mp3",
  },
  copy: {
    en: {
      label: "Audio conversation · 23:39 · English",
      title: "Why Illness Is Not a Battle",
      description: "A two-host discussion of There Is No Last Arena, created with Google NotebookLM and checked against the original essay for semantic accuracy. This is an interpretation of the argument, not a verbatim reading.",
      playerLabel: "Play the English audio conversation Why Illness Is Not a Battle",
      chapters: "Listen by idea",
      transcript: "Full transcript",
      download: "Download MP3",
      play: "Play audio",
      pause: "Pause audio",
      close: "Close and stop the compact player",
      seek: "Audio progress",
      transcriptEyebrow: "Audio companion · Essay 06",
      transcriptTitle: "Why Illness Is Not a Battle",
      transcriptLead: "Listen while reading the complete transcript. Choose a language below; every timestamp jumps to the exact moment in the English conversation.",
      transcriptNote: "This conversation was created with Google NotebookLM and checked against the original essay. It interprets the argument; it is not a verbatim reading of the essay and is not medical or legal advice.",
      transcriptLanguage: "Transcript language",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Loading the transcript…",
      loadError: "The transcript could not be loaded. Please try again, or open the English original.",
      retry: "Try again",
      backToEssay: "Return to Essay 06",
      jumpToTime: (time: string) => `Play from ${time}`,
    },
    uk: {
      label: "Аудіорозмова · 23:39 · англійською",
      title: "Чому хвороба — не битва",
      description: "Розмова двох ведучих про есе «Останньої арени немає», створена за допомогою Google NotebookLM і перевірена на відповідність змісту оригіналу. Це інтерпретація аргументу, а не дослівне читання есе.",
      playerLabel: "Відтворити англомовну аудіорозмову Чому хвороба — не битва",
      chapters: "Слухати за темами",
      transcript: "Повна транскрипція",
      download: "Завантажити MP3",
      play: "Відтворити аудіо",
      pause: "Призупинити аудіо",
      close: "Закрити й зупинити компактний програвач",
      seek: "Перебіг аудіо",
      transcriptEyebrow: "Аудіосупровід · Есе 06",
      transcriptTitle: "Чому хвороба — не битва",
      transcriptLead: "Слухайте й читайте повну транскрипцію. Оберіть мову нижче; кожен таймкод веде до точного місця в англомовній розмові.",
      transcriptNote: "Цю розмову створено за допомогою Google NotebookLM і перевірено за оригінальним есе. Вона інтерпретує аргумент, а не читає есе дослівно, і не є медичною чи юридичною порадою.",
      transcriptLanguage: "Мова транскрипції",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Завантажуємо транскрипцію…",
      loadError: "Не вдалося завантажити транскрипцію. Спробуйте ще раз або відкрийте англійський оригінал.",
      retry: "Спробувати ще раз",
      backToEssay: "Повернутися до есе 06",
      jumpToTime: (time: string) => `Відтворити з ${time}`,
    },
    ru: {
      label: "Аудиоразговор · 23:39 · на английском",
      title: "Почему болезнь — не битва",
      description: "Разговор двух ведущих об эссе «Последней арены нет», созданный с помощью Google NotebookLM. Это интерпретация аргумента, а не дословное чтение эссе.",
      playerLabel: "Воспроизвести англоязычный аудиоразговор Почему болезнь — не битва",
      chapters: "Слушать по темам",
      transcript: "Полная транскрипция",
      download: "Скачать MP3",
      play: "Воспроизвести аудио",
      pause: "Приостановить аудио",
      close: "Закрыть и остановить компактный проигрыватель",
      seek: "Ход аудио",
      transcriptEyebrow: "Аудиосопровождение · Эссе 06",
      transcriptTitle: "Почему болезнь — не битва",
      transcriptLead: "Слушайте и читайте полную транскрипцию. Выберите язык ниже; каждый таймкод ведёт к точному месту в англоязычном разговоре.",
      transcriptNote: "Этот разговор создан с помощью Google NotebookLM и проверен по оригинальному эссе. Он интерпретирует аргумент, а не читает эссе дословно, и не является медицинской или юридической рекомендацией.",
      transcriptLanguage: "Язык транскрипции",
      englishOriginal: "English original",
      ukrainianTranslation: "Український переклад",
      russianTranslation: "Русский перевод",
      loading: "Загружаем транскрипцию…",
      loadError: "Не удалось загрузить транскрипцию. Попробуйте ещё раз или откройте английский оригинал.",
      retry: "Попробовать ещё раз",
      backToEssay: "Вернуться к эссе 06",
      jumpToTime: (time: string) => `Воспроизвести с ${time}`,
    },
  },
} satisfies EssayAudio;

/**
 * The complete audio catalogue. Adding another essay audio experience only
 * requires adding one data record here and its public media/transcript files.
 */
export const essayAudioEntries: readonly EssayAudio[] = [essay01Audio, essay02Audio, essay03Audio, essay04Audio, essay05Audio, essay06Audio];

const essayAudioBySlug = new Map(essayAudioEntries.map((audio) => [audio.slug, audio]));

export function getEssayAudio(slug: string | undefined) {
  return slug ? essayAudioBySlug.get(slug) : undefined;
}

export function essayAudioRoute(audio: EssayAudio) {
  return `/essay/${audio.slug}/audio`;
}

export function matchEssayAudioRoute(pathname: string) {
  const match = pathname.match(/^\/essay\/([^/]+)\/audio\/?$/);
  return match ? getEssayAudio(match[1]) : undefined;
}

export function transcriptSource(audio: EssayAudio, language: Language) {
  return `${audio.publicDirectory}/transcript-${language}.json`;
}

// Backwards-compatible aliases for the first published audio companion.
export const ESSAY_AUDIO_SLUG = essay06Audio.slug;
export const ESSAY_AUDIO_ID = essay06Audio.audioId;
export const ESSAY_AUDIO_DURATION = essay06Audio.duration;
export const ESSAY_AUDIO_ROUTE = essayAudioRoute(essay06Audio);
export const essayAudioChapters = essay06Audio.chapters;
export const essayAudioSources = essay06Audio.sources;
export const essayAudioCopy = essay06Audio.copy;

export function formatAudioTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const rounded = Math.floor(seconds);
  const minutes = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}
